/**
 * Anthropic's Claude Certification ladder — content for
 * /academy/claude-certification/.
 *
 * WHY THIS PAGE EXISTS. We already sell Claude certification prep (see
 * academy-courses.ts) and were invisible on every certification query in both
 * Malaysia and Singapore — absent from the top 20 and from the AI Overview on
 * `claude certification course malaysia`. Meanwhile Agmo runs a dedicated domain
 * (claudeaimalaysia.com) whose "4 Certifications Explained" page holds the term.
 * We sold the thing and targeted the word nowhere.
 *
 * ================= SOURCING — READ BEFORE CHANGING ANY FEE =================
 *
 * PRIMARY SOURCE: Anthropic Partner Academy, https://anthropic-partners.skilljar.com/
 * (retrieved 2026-09-13). Every fee, question count, duration, validity period and
 * retake rule below is read from that site's own published exam data — which states
 * "$99/$125/$175 prices confirmed 2026-07-08 and set on the live offers".
 *
 * An earlier draft carried these figures from six third-party summaries because
 * anthropic.com/certification, /learn/certification and /partners/academy all
 * return 404 and no vendor page could be found. The partner-academy URL is the one
 * that works; the third-party figures happened to be correct, but that was luck and
 * the hedge was right until it was checked. Re-verify here, not on a blog, before
 * changing anything.
 *
 * Still dated on the page ("as published, September 2026") because Anthropic moved
 * exam delivery from ProctorFree to Pearson in June 2026 and lists the refund policy
 * as TBD — this programme is still changing.
 *
 * WHAT WE DO NOT PUBLISH (Jazz, 2026-09-13): our own course fee. Course pricing
 * is on enquiry. Do not add a course price here, infer one, or imply a range —
 * the only number on this page is Anthropic's exam fee, which is public.
 *
 * WHAT WE MUST NOT CLAIM. We prepare people for Anthropic's exams as a firm Certified in
 * the Claude Partner Network. We are NOT the certifying body, not an
 * official/exclusive/endorsed training partner, and we do not administer the
 * exam — Pearson VUE proctors it. Never write "official Claude certification
 * provider" or anything a reader could mistake for it.
 *
 * MALAYSIA vs SINGAPORE. HRD Corp claimability is a Malaysian-entity fact and a
 * genuine advantage. Anchor Sprint Pte. Ltd. is NOT a SkillsFuture Registered
 * Training Provider — see singapore/claude-training.ts, which states this
 * plainly. Never imply SkillsFuture eligibility on this page.
 */

export interface ClaudeExam {
  /** Anthropic's exam code, as published. */
  code: string;
  name: string;
  /** Exam fee per attempt, in USD. Anthropic Partner Academy, Sept 2026. */
  fee: string;
  /** Question count and duration, from the published exam data. */
  format: string;
  audience: string;
  covers: string;
  /** Our prep course tier in academy-courses.ts, where we run one. */
  prepTier?: string;
  /**
   * Scored pass mark, where Anthropic's exam guide publishes one [C36].
   * Only CCAR-F is sourced — do NOT infer the others' pass marks from it.
   */
  passMark?: string;
  /** Scenario structure, where the guide publishes it [C36]. */
  scenarios?: string;
  /**
   * Weighted domain blueprint from the exam guide [C37]. Sourced for CCAR-F only.
   * This is the detail that makes the page more useful than a competitor's fee table:
   * it doubles as a vetting script for anyone evaluating an AI partner.
   */
  blueprint?: Array<{ domain: string; weight: string }>;
}

export const claudeExams: ClaudeExam[] = [
  {
    code: "CCAO-F",
    name: "Claude Certified Associate — Foundations",
    fee: "USD 99",
    format: "60 questions · 120 minutes",
    audience: "Business and non-technical knowledge workers",
    covers:
      "Everyday Claude use, Projects, Skills and Claude Cowork. The business-user exam — no code required.",
    prepTier: "A1",
  },
  {
    code: "CCDV-F",
    name: "Claude Certified Developer — Foundations",
    fee: "USD 125",
    format: "53 questions · 120 minutes",
    audience: "Software engineers building on the Claude API",
    covers:
      "The Messages API, tool use, structured outputs, prompt caching and streaming — writing code against Claude rather than using it.",
    prepTier: "A3",
  },
  {
    code: "CCAR-F",
    name: "Claude Certified Architect — Foundations",
    fee: "USD 125",
    format: "60 questions · 120 minutes",
    audience: "Engineers and solution architects",
    covers:
      "Agent design, Agent Skills, MCP and the system decisions behind a production deployment.",
    prepTier: "A2",
    passMark: "Scaled score of 720 on a 100–1,000 scale",
    scenarios: "4 scenarios drawn from a bank of 6",
    blueprint: [
      { domain: "Agentic architecture & orchestration", weight: "27%" },
      { domain: "Claude Code configuration & workflows", weight: "20%" },
      { domain: "Prompt engineering & structured output", weight: "20%" },
      { domain: "Tool design & MCP integration", weight: "18%" },
      { domain: "Context management & reliability", weight: "15%" },
    ],
  },
  {
    code: "CCAR-P",
    name: "Claude Certified Architect — Professional",
    fee: "USD 175",
    format: "63 questions · 120 minutes",
    audience: "Senior architects running production agentic systems",
    covers:
      "The advanced architect tier — production-grade agentic workflows and MCP at scale. We do not currently run a prep course for this one.",
  },
];

/** Shown wherever a fee appears. The hedge is deliberate — see the header. */
export const examFeeNote =
  "Exam fees are Anthropic's, charged per attempt and paid through the Anthropic Partner Academy, shown here as published in September 2026 — worth confirming at registration, since delivery moved to Pearson in June 2026 and the refund policy is still listed as to be confirmed. Every exam runs 120 minutes in English, online proctored or at a Pearson test centre. Our course fee is separate and quoted on enquiry; where we run a prep course for an exam, the exam attempt is included in that fee.";

/**
 * Validity and retakes — read from the Partner Academy's own FAQ. Competitors'
 * pages and the AI Overview omit both, and they are the first things a manager
 * paying for a cohort asks. Publishing them is cheap and differentiating.
 */
/**
 * Credential delivery [C39]. Stated because "certified" is claimed loosely in this market
 * and a Credly badge is independently checkable by a buyer.
 */
export const badgeNote =
  "Successful candidates receive a digital badge through Credly, so a client can verify the credential themselves rather than take a vendor's word for it.";

export const validityNote =
  "A Claude certification is valid for 12 months from the date it is earned. Renewing on time is free: you review what has changed since you certified and complete a free, non-proctored assessment — there is no second exam fee unless you let it lapse. Budget for the certification as an annual thing, not a one-off.";

export const retakeNote =
  "If someone does not pass, the waiting period before a retake grows with each attempt — 14 days after the first, 30 after the second, 90 after the third — with a maximum of four attempts in a rolling 12 months and the full exam fee payable each time. Candidates who pass cannot resit to improve a score. This is exactly why we would rather tell you a cohort is not ready than book them in.";

/** Anthropic's own published caveat. Stated because a buyer could reasonably assume otherwise. */
export const partnerTierNote =
  "One thing Anthropic states plainly and most training pages leave out: the Claude Certified Associate certification does not count towards Claude Partner Network tier eligibility. If your goal is partner status rather than capability, certifying your business users will not move it.";

/** The differentiator worth being explicit about. */
export const bundledExamNote =
  "Most providers quote the course and leave you to pay Anthropic separately. On the three tracks we run, the exam attempt is included in the course fee — so the number we quote is the number you pay.";

/** Which role should take which exam — the question the SERP is actually asking. */
export const trackGuidance = [
  {
    role: "Operations, marketing, finance, HR, team leads",
    take: "Associate — Foundations (CCAO-F)",
    why: "You want Claude to do your existing job faster. No code, and the capstone is built on your own workflow rather than a sample.",
  },
  {
    role: "Software engineers",
    take: "Developer — Foundations (CCDV-F)",
    why: "You are writing code against the Claude API. More hands-on than Associate, less system design than Architect.",
  },
  {
    role: "Solution architects, technical leads",
    take: "Architect — Foundations (CCAR-F)",
    why: "You are deciding how agents, Skills and MCP fit together and what runs in production.",
  },
  {
    role: "Senior architects already running agents in production",
    take: "Architect — Professional (CCAR-P)",
    why: "The advanced tier. We do not run a prep course for it — if this is the target, tell us and we will say honestly whether our Architect Foundations course is the right step first.",
  },
];

export const certificationFaqs = [
  {
    question: "How much does Claude certification cost in Malaysia?",
    answer:
      "Anthropic's exam fees, as published in September 2026, are USD 99 for the Claude Certified Associate — Foundations (CCAO-F), USD 125 for Developer — Foundations (CCDV-F), USD 125 for Architect — Foundations (CCAR-F) and USD 175 for Architect — Professional (CCAR-P), charged per attempt and proctored by Pearson VUE. Training is separate and priced on enquiry. On the three tracks Anchor Sprint runs, the exam attempt is included in the course fee, and training delivered in Malaysia is claimable against your HRD Corp levy — so the out-of-pocket cost for a Malaysian employer is usually far lower than the list figures suggest.",
  },
  {
    question: "Which Claude certification should I take?",
    answer:
      "If you are in operations, marketing, finance, HR or leading a team, take the Associate — it is the business-user exam and needs no code. If you write software against the Claude API, take the Developer. If you design how agents, Skills and MCP fit together, take the Architect — Foundations. The Architect — Professional tier is for people already running agentic systems in production. Tell us the roles on your team and we will map them rather than sell you the most expensive track.",
  },
  {
    question: "Is Claude certification training HRD Corp claimable?",
    answer:
      "Yes for training delivered in Malaysia. Anchor Sprint Sdn Bhd is an HRD Corp approved training provider, so our Claude certification prep can be claimed against your levy, and we file the grant and the claim rather than handing you a form. Singapore is different and we would rather say so plainly: Anchor Sprint Pte. Ltd. is not a SkillsFuture Registered Training Provider, so training delivered in Singapore is commercial and not SkillsFuture-claimable.",
  },
  {
    question: "Do you administer the Claude certification exam?",
    answer:
      "No. Anthropic owns the certification and the exams are proctored by Pearson VUE. Anchor Sprint is Certified in the Claude Partner Network and we prepare people for those exams — our courses are taught from the official Partner Academy paths, and on the tracks we run the exam attempt is included in the course fee. We are not the certifying body and could not award a credential if we wanted to.",
  },
  {
    question: "Can you run Claude certification training in-house for our team?",
    answer:
      "That is most of what we do. Corporate cohorts run at your office or remotely across Malaysia and Singapore, and the capstone is built on your own workflows rather than a generic sample — which is the part that makes the training survive contact with Monday. Tell us the team size, the roles and which exams you are targeting and we will come back with a scoped programme and a quotation.",
  },
  {
    question: "How long is a Claude certification valid for?",
    answer:
      "Twelve months from the date it is earned. Renewing on time is free — you review what has changed since certifying and complete a free, non-proctored assessment, with no second exam fee unless you let it lapse. Worth knowing before you budget: this is an annual commitment, not a one-off, and most training pages do not mention it.",
  },
  {
    question: "What happens if someone fails the Claude certification exam?",
    answer:
      "They can retake it, but the waiting period grows with each attempt — 14 days after the first, 30 after the second, 90 after the third — with a maximum of four attempts in a rolling 12 months and the full exam fee payable each time. Candidates who pass cannot resit to improve a score. That is why we will tell you plainly if a cohort looks under-prepared rather than book them into an exam and let the waiting periods do the damage.",
  },
  {
    question: "How long does it take to prepare for a Claude certification?",
    answer:
      "Our prep courses run two days each. Whether that is enough depends on where you start: an operations manager who already uses Claude daily is usually ready for the Associate exam straight after, while an engineer new to the Claude API will want practice time between the Developer course and the exam. We will tell you honestly if a cohort looks under-prepared rather than book them into an exam they will fail.",
  },
];
